/**
 * Format: Product
 * -----------------------------------------------------------------------------
 * Formats Shopify storefront API product responses.
 *
 */
import { formatImage, formatImages } from '~/format/format-image';
import { formatMedia } from '~/format/format-media';
import { formatVideo } from '~/format/format-video';
import { formatOptions } from '~/format/format-option';
import { formatProductPriceRange } from '~/format/format-price';
import { formatVariants } from '~/format/format-variant';
import { decodeApiId, transformEdges } from '~/helpers/graphql';
import { formatRichtext } from '~/core/helpers/format-richtext';

/**
 * Format product.
 * - https://shopify.dev/docs/api/storefront/2024-01/objects/Product
 * @param {Object} response - GraphQL response.
 * @param {Object} [data.collection] - Formatted collection object.
 * @param {Array} [data.exclude] - Array of variant GIDs to exclude when
 * selecting product variant.
 * @param {Number} [data.variantId] - Variant ID to force selection of variant.
 * @param {String} [data.variantTitle] - Variant title to use for the selected variant.
 * @param {Object} [data.variantImage] - Variant image to use for the selected variant.
 * @param {Boolean} [data.variantAvailable] - Variant availability status to use for the selected variant.
 * @returns {Object}
 */
export function formatProduct(
  response,
  {
    collection,
    exclude,
    variantId,
    variantTitle,
    variantImage,
    variantAvailable,
  } = {},
) {
  if (!response) {
    return {};
  }

  const product = response.product ? response.product : response;

  /**
   * Properties set to null weren't being loaded in GraphQL anyway.
   */
  const formattedProduct = {
    applicationContent: null,
    available: product.available,
    availableState: product.available ? 'available' : 'sold-out',
    bundle: formatProductBundles(product),
    badges: formatProductBadges(product),
    hiddenProduct: product.hiddenProduct,
    seoHidden: product.seoHidden,
    upsizing:
      product.upsizing?.value.split(',').map((value) => value.trim()) ?? [],
    collectionIds: transformEdges(product.collections).map(
      (collectionObject) => {
        return decodeApiId(collectionObject?.id);
      },
    ),
    compareAtPrice: formatProductPriceRange(product.compareAtPriceRange),
    description: product.descriptionHtml,
    descriptionFiles: null,
    gid: product.id,
    giftCard: product.giftCard,
    goalChips: null,
    handle: product.handle,
    hasOnlyDefaultVariant: false,
    id: decodeApiId(product.id),
    isVariant: Boolean(variantId),
    media: formatMedia(product.media?.references, false, response.product),
    price: formatProductPriceRange(product.priceRange),
    reviews: {
      count: product.reviewsCount?.value ?? 0,
      rating: product.reviewsAverage?.value
        ? JSON.parse(product.reviewsAverage.value).value
        : 0,
    },
    subtitle: product.subtitle?.value,
    consumptionRecommendation: product.consumptionRecommendation?.value,
    tags: product.tags,
    title: product.title,
    type: product.productType ?? false,
    variants: formatVariants(product, collection),
    vendor: product.vendor,
    disableFeaturedBannerMarkets:
      product.disableFeaturedBannerMarkets?.value
        .split(',')
        .map((value) => value.trim().toUpperCase()) ?? [],
  };

  /**
   * PLP featured banner.
   */
  const featuredBanner = product.featuredBanner?.reference?.image;

  if (featuredBanner) {
    formattedProduct.featuredBanner = formatImage(featuredBanner);
  }

  /**
   * Product social selling.
   */
  const socialSellingObject = product.socialSelling?.reference?.fields;
  let icon = '';
  let description = '';

  if (socialSellingObject) {
    socialSellingObject.forEach((social) => {
      if (social.key === 'icon') {
        icon = social.reference.image.url;
      }

      if (social.key === 'description') {
        description = formatRichtext(social.value);
      }
    });
  }

  if (icon && description) {
    formattedProduct.socialSelling = {
      icon,
      description,
    };
  }

  /**
   * Product video
   */
  const videos = [];
  const videoMetaobject = product.videos?.reference?.fields;

  if (videoMetaobject) {
    videoMetaobject.forEach((video) => {
      if (video.reference) {
        videos.push(formatVideo(video.reference));
      }
    });
  }

  formattedProduct.videos = videos;

  /**
   * Product card featured image.
   */
  const featuredImage = formatImages(
    product,
    transformEdges(product.featuredImage),
  );

  formattedProduct.featuredImage = featuredImage.length
    ? featuredImage[0]
    : featuredImage;

  /**
   * Default variants and options.
   */
  formattedProduct.options = formatOptions(product, formattedProduct.variants);

  if (
    (formattedProduct.options?.length === 1 &&
      formattedProduct.options[0]?.name?.toLowerCase() === 'title') ||
    (formattedProduct.variants?.length === 1 &&
      formattedProduct.variants[0]?.title?.toLowerCase() === 'default title')
  ) {
    formattedProduct.hasOnlyDefaultVariant = true;
  }

  /**
   * Get selected variant.
   */
  formattedProduct.selectedOrFirstAvailableVariant =
    getSelectedOrFirstAvailableVariant(formattedProduct, variantId, exclude);

  // If we have a variant title from the metaobject, override the variant's title
  if (variantTitle && formattedProduct.selectedOrFirstAvailableVariant) {
    formattedProduct.selectedOrFirstAvailableVariant.title = variantTitle;
  }

  // If we have a variant image from the metaobject, override the variant's image
  if (variantImage && formattedProduct.selectedOrFirstAvailableVariant) {
    formattedProduct.selectedOrFirstAvailableVariant.featuredImage =
      formatImage(variantImage);
  }

  // If we have a variant availability status from the metaobject, override the variant's availability
  if (
    typeof variantAvailable !== 'undefined' &&
    formattedProduct.selectedOrFirstAvailableVariant
  ) {
    formattedProduct.selectedOrFirstAvailableVariant.available =
      variantAvailable;
    formattedProduct.selectedOrFirstAvailableVariant.availableState =
      variantAvailable ? 'available' : 'sold-out';
  }

  // Ensure the variant URL is set correctly when we have a variant
  if (variantId && formattedProduct.selectedOrFirstAvailableVariant) {
    formattedProduct.selectedOrFirstAvailableVariant.url = `//${location.host}${cnvs.routes.normalisedRoot}products/${formattedProduct.handle}?variant=${variantId}`;
  }

  const allSoldout = formattedProduct.variants.every(
    (variant) => variant.availableState === 'sold-out' || !variant.available,
  );
  if (allSoldout) {
    formattedProduct.availableState = 'sold-out';
  }

  /**
   * If no variant is found or specified variant is hidden then hide product.
   */
  if (
    !formattedProduct.selectedOrFirstAvailableVariant ||
    (variantId &&
      formattedProduct.selectedOrFirstAvailableVariant.availableState ===
        'hidden')
  ) {
    formattedProduct.availableState = 'hidden';

    if (!formattedProduct.selectedOrFirstAvailableVariant) {
      formattedProduct.selectedOrFirstAvailableVariant =
        formattedProduct.variants[0];
    }
  }

  /**
   * Determine price object (for product cards).
   */
  formattedProduct.priceObject = getPriceObject(formattedProduct, variantId);

  /**
   * Determine URL.
   */
  const url = `//${location.host}${cnvs.routes.normalisedRoot}products/${product.handle}`;

  formattedProduct.url = formatProductUrl(url, product.trackingParameters);

  /**
   * Product information composition image.
   */
  formattedProduct.compositionImage = null;

  return formattedProduct;
}

/**
 * Format product bundles.
 * @param {Object} product - Unformatted product.
 * @returns {Array}
 */
export function formatProductBundles(product) {
  if (!product.bundle) {
    return [];
  }

  return product.bundle?.references?.nodes.map((item) => {
    let isSoldout = false;
    const ukMarket = cnvs.store.marketCode === 'UK';
    const now = new Date().getTime();
    let nodeShowSoldOut = false;
    let nodeShowSoldOutPublishDate = 0;

    for (const node of item.field.references.nodes) {
      // Check if variant should be show sold out based on metafields
      if (node.metafields) {
        const nodeMetafields = node.metafields.filter((nm) => Boolean(nm));

        if (nodeMetafields.length > 0) {
          nodeMetafields.forEach((metafield) => {
            if (ukMarket) {
              if (
                metafield.key === 'show_sold_out_uk' &&
                metafield.value === 'true'
              ) {
                nodeShowSoldOut = true;
                node.availableForSale = false;
              }
              if (metafield.key === 'set_relaunch_date_uk') {
                nodeShowSoldOutPublishDate = new Date(
                  metafield.value,
                ).getTime();
              }
            } else {
              if (
                metafield.key === 'show_sold_out_elmshorn' &&
                metafield.value === 'true'
              ) {
                nodeShowSoldOut = true;
                node.availableForSale = false;
              }
              if (metafield.key === 'set_relaunch_date_elmshorn') {
                nodeShowSoldOutPublishDate = new Date(
                  metafield.value,
                ).getTime();
              }
            }
            if (nodeShowSoldOut === true && nodeShowSoldOutPublishDate > 0) {
              if (now > nodeShowSoldOutPublishDate) {
                node.availableForSale = true;
              }
            }
          });
        }
      }
    }

    if (
      item.field.references.nodes.every(
        (node) => node.availableForSale === false,
      )
    ) {
      isSoldout = true;
    }
    return { isSoldout };
  });
}

/**
 * Format product badges.
 * @param {Object} product - Unformatted product.
 * @returns {Array}
 */
export function formatProductBadges(product) {
  if (!product.badges) {
    return [];
  }

  return product.badges?.references?.nodes.map(
    (badge) => badge.fields[0]?.value,
  );
}

/**
 * Get selected or first available variant.
 * - 'Selected' based on URL parameter or passed ID.
 * - If no selected then find available 908g variant with no discount which
 *   doesn't have a price of 0.
 * @param {Object} product - Formatted product object.
 * @param {Number} [variantId] - Variant ID to force selection of variant.
 * @param {Array} [exclude] - Array of variant GIDs to exclude when selecting
 * product variant, URL parameter overrides excluded IDs.
 * @returns {Object}
 */
export function getSelectedOrFirstAvailableVariant(
  product,
  variantId,
  exclude,
) {
  const urlSearchParams = new URLSearchParams(location.search);

  let selectedVariantId = urlSearchParams.has('variant')
    ? urlSearchParams.get('variant')
    : false;

  if (variantId) {
    selectedVariantId = variantId;
  }

  /**
   * Find matching variant based on passed ID or URL parameter.
   */
  const selectedVariant = product.variants?.find((variant) => {
    return selectedVariantId ? variant.id === Number(selectedVariantId) : false;
  });

  // Check if the selected variant is available
  if (
    selectedVariant &&
    selectedVariant.available &&
    selectedVariant.availableState === 'available'
  ) {
    return selectedVariant;
  }

  // If the selected variant is not available, find the first available variant
  const firstAvailableVariant = product.variants?.find(
    (variant) => variant.available && variant.availableState === 'available',
  );

  if (firstAvailableVariant) {
    return firstAvailableVariant;
  }

  /**
   * Find alternate variants.
   * - If all else fails, return false as this product shouldn't be displayed.
   */
  const options = [
    // Find available, non-sale 908g variant
    { available: true, defaultSize: true, nonSale: true },
    // Otherwise find available non-sale, non-sample variant
    { available: true, defaultSize: false, nonSale: true },
    // Otherwise find out of stock, non-sale 908g variant
    { available: false, defaultSize: true, nonSale: true },
    // Otherwise find out of stock, non-sale, non-sample variant
    { available: false, defaultSize: false, nonSale: true },
    // Otherwise find available 908g variant
    { available: true, defaultSize: true, nonSale: false },
    // Otherwise find available non-sample variant,
    { available: true, defaultSize: false, nonSale: false },
    // Otherwise find out of stock 908g variant
    { available: false, defaultSize: true, nonSale: false },
    // Otherwise find out of stock non-sample variant
    { available: false, defaultSize: false, nonSale: false },
  ];

  let alternateVariant = false;

  for (const option of options) {
    alternateVariant = findVariant(product.variants, option, exclude);

    if (alternateVariant) {
      break;
    }
  }

  return alternateVariant;
}

/**
 * Find variant based on conditions.
 * @param {Array} variants - Product variants.
 * @param {Object} [options] - Find options.
 * @param {Boolean} [options.available] - Variant is available?
 * @param {Boolean} [options.defaultSize] - Variant has default size title?
 * @param {Boolean} [options.nonSale] - Variant is non-sale?
 * @param {Array} [exclude] - Array of variant GIDs to exclude when selecting
 * product variant, URL parameter overrides excluded IDs.
 * @returns {Object}
 */
function findVariant(
  variants,
  { available = true, defaultSize = true, nonSale = true } = {},
  exclude = [],
) {
  return variants?.find((variant) => {
    const availableCondition = available
      ? variant.available
      : ['back-soon', 'sold-out'].includes(variant.availableState);

    const nonSaleCondition = nonSale ? !variant.compareAtPriceDiscount : true;

    const defaultSizeCondition = defaultSize
      ? variant.title.includes(cnvs.product.defaultSizeValue)
      : !isSampleVariant(variant.title);

    return (
      availableCondition &&
      nonSaleCondition &&
      defaultSizeCondition &&
      variant.price.amount &&
      !exclude?.includes(variant.gid)
    );
  });
}

/**
 * Get price object.
 * - Sorts variants so price object contains starting price and associated
 *   variant's compare at price.
 * @param {Object} product - Formatted product object.
 * @param {Number} [variantId] - Variant ID to force selection of variant.
 * @returns {Object}
 */
function getPriceObject(product, variantId) {
  if (variantId) {
    return {
      compareAtPrice:
        product.selectedOrFirstAvailableVariant?.compareAtPrice?.amount,
      price: product.selectedOrFirstAvailableVariant?.price?.amount,
      varies: false,
    };
  }

  /**
   * Find 'from' price
   * - Sort variant prices and use lowest.
   * - Ignore sale, sample, and hidden variants.
   * - Ignore sold out variants if possible.
   */
  let compareAtPrice = product?.compareAtPrice?.amount;
  let price = product?.price?.amount;
  let varies = product?.price?.varies;

  if (product?.variants?.length > 1) {
    const variants = Array.from(product?.variants);

    const priceSortedVariants = variants
      .filter((variant) => {
        return (
          variant.availableState !== 'hidden' &&
          !variant.compareAtPriceDiscount &&
          variant.price.amount &&
          !isSampleVariant(variant.title)
        );
      })
      .sort((a, b) => a.price?.amount - b.price?.amount);

    /**
     * Filter sold out variants in second array.
     */
    const notSoldOutPriceSortedVariants = priceSortedVariants.filter(
      (variant) => {
        return variant.availableState === 'available';
      },
    );

    let priceVariants = priceSortedVariants;

    /**
     * If non-sold out array has length then use it.
     * - Falls back to sold out variants if that has length.
     * - Use product price and varies as emergency fallback.
     */
    if (notSoldOutPriceSortedVariants.length) {
      priceVariants = notSoldOutPriceSortedVariants;
      compareAtPrice = notSoldOutPriceSortedVariants[0]?.compareAtPrice?.amount;
      price = notSoldOutPriceSortedVariants[0]?.price?.amount;
    } else if (priceSortedVariants.length) {
      compareAtPrice = priceSortedVariants[0]?.compareAtPrice?.amount;
      price = priceSortedVariants[0]?.price?.amount;
    }

    /**
     * Determine if price still varies after all the filtering.
     */
    if (priceVariants.length) {
      const uniquePrices = [];

      priceVariants.forEach((variant) => {
        if (uniquePrices.includes(variant.price?.amount)) {
          return;
        }

        uniquePrices.push(variant.price?.amount);
      });

      varies = uniquePrices.length > 1;
    }
  }

  return {
    compareAtPrice,
    price,
    varies,
  };
}

/**
 * Format product URL with tracking parameters.
 * - https://shopify.dev/docs/api/storefront/2024-01/objects/Product#field-product-trackingparameters
 * @param {String} url - The URL of the object.
 * @param {Null|String} [trackingParameters] - Response tracking parameters.
 * @returns {String}
 */
export function formatProductUrl(url, trackingParameters) {
  if (!trackingParameters) {
    return url;
  }

  /**
   * Storefront API doesn't include https in URL but new URL() requires it.
   */
  const localUrl = `https://${url.split('//')[1]}`;
  const urlObject = new URL(localUrl);
  const trackingSearchParams = new URLSearchParams(trackingParameters);

  const parameters = new URLSearchParams({
    ...Object.fromEntries(urlObject.searchParams),
    ...Object.fromEntries(trackingSearchParams),
  });

  if (!parameters.size) {
    return url;
  }

  const href = urlObject.href.split('?')[0].replace('https:', '');

  return `${href}?${parameters.toString()}`;
}

/**
 * Determine if variant title includes word set as sample size setting.
 * @param {String} variantTitle - Variant title to test.
 * @returns {Boolean}
 */
function isSampleVariant(variantTitle) {
  return cnvs.product.sampleSizeValue.some((value) => {
    return variantTitle.includes(value);
  });
}
